import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, Icon, Progress, Spinner, Text, VStack, useToast } from '@chakra-ui/react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getReferenceData, listProcesses, reviewStatement } from '../../../api/endpointsBackend';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { useMovementDetails, useStatementCheck, useStatementRepairFlags } from '../../../hooks/useStatementRepair';
import useIsPhone from '../../../hooks/useIsPhone';
import { sheetBacksSettled } from '../../../hooks/useSheetHistory';
import { formatRelativeAgo } from '../../../utils/intlFormatters';
import { fileMeta } from '../../source-files/sourceFilesUI';
import { errorKey } from '../repairsUI';
import CheckupDone from './CheckupDone';
import CheckupReview from './CheckupReview';
import PhoneCheckupSheet from './PhoneCheckupSheet';
import { DECIDABLE_RUNS, checkupQueue, chooseAll, initialChoices, nextInQueue, readTwice } from './checkupModel';

const LIST_PATH = '/source-files';

// The statements' names and upload dates, by source file id, from the same
// list the Statements view loads. A statement past it is named by its id.
function useStatementNames() {
  const [byFile, setByFile] = useState({});
  useEffect(() => {
    listProcesses({ pageSize: 100 })
      .then((result) => {
        const found = {};
        (result.processes || []).forEach((process) => {
          const file = process.source_file;
          if (!file?.id || found[file.id]) return;
          found[file.id] = {
            filename: file.filename,
            fileType: file.file_type,
            uploaded: file.upload_date || process.started_at || null,
            accounts: (process.accounts || []).map((account) => account.name).filter(Boolean),
          };
        });
        setByFile(found);
      })
      .catch(() => {});
  }, []);
  return byFile;
}

const nameOf = (meta, id, t) => meta?.accounts?.[0] || meta?.filename || t('checkup.statement.unknown', { id });

// The movement ids a check names, so the review can show them by name; not
// the ones it already knows are gone (a movement an applied check removed).
const movementIds = (run) => {
  const items = run?.items || [];
  const gone = new Set(items.flatMap((item) => (item.movements || []).filter((m) => m.deleted).map((m) => m.id)));
  return items
    .flatMap((item) => [item.change?.movement_id, ...(item.movements || []).map((movement) => movement.id)])
    .filter((id) => id != null && !gone.has(id));
};

function TopBar({ index, count, done, onBack, onSkip, canSkip }) {
  const { t } = useTranslation('repairs');
  const position = index != null ? t('checkup.top.position', { index: index + 1, count }) : null;
  return (
    <Flex
      align="center"
      justify="space-between"
      gap={3}
      py={3}
      mb={[3, 5]}
      borderBottom="1px solid"
      borderColor="redesign.border"
    >
      <Button size="sm" variant="ghost" leftIcon={<LuChevronLeft />} onClick={onBack}>
        {t('checkup.top.back')}
      </Button>
      {count > 0 && (
        <Flex align="center" gap={3} flex={1} justify="center" minW={0}>
          <Text fontSize="xs" color="redesign.text.muted" display={['none', 'block']}>{t('checkup.top.label')}</Text>
          <Progress
            value={count ? (done / count) * 100 : 0}
            size="sm"
            borderRadius="full"
            w={['80px', '200px']}
            bg="redesign.bg.sunken"
            sx={{ '& > div': { bg: 'redesign.accent.blue' } }}
            aria-label={t('checkup.top.done', { count: done })}
          />
          {position && <Text fontSize="xs" fontWeight={600} color="redesign.heading" whiteSpace="nowrap">{position}</Text>}
        </Flex>
      )}
      {canSkip ? (
        <Button size="sm" variant="ghost" rightIcon={<LuChevronRight />} onClick={onSkip}>
          <Text as="span" display={['none', 'inline']}>{t('checkup.top.skip')}</Text>
        </Button>
      ) : <Box w={['40px', '120px']} />}
    </Flex>
  );
}

// The line under a statement's name: its file, when it was uploaded and,
// after a re-read, when it was checked.
function statementLine(meta, readAt, t) {
  // "uploaded 2 minutes ago": the relative time starts lower case mid-line.
  const ago = (value) => {
    const text = formatRelativeAgo(value) || '';
    return text.charAt(0).toLowerCase() + text.slice(1);
  };
  return [
    meta?.filename,
    meta?.uploaded && t('checkup.statement.uploaded', { when: ago(meta.uploaded) }),
    readAt && t('checkup.statement.readAgain', { when: ago(readAt) }),
  ].filter(Boolean).join(' · ');
}

function StatementHeader({ id, meta, readAt }) {
  const { t } = useTranslation('repairs');
  const file = fileMeta(meta?.fileType);
  const line = statementLine(meta, readAt, t);
  return (
    <Flex align="center" gap={3} mb={4}>
      <Flex w="44px" h="44px" borderRadius="10px" bg={file.bg} _dark={file._dark} align="center" justify="center" flexShrink={0}>
        <Icon as={file.icon} boxSize="20px" color={file.color} />
      </Flex>
      <Box minW={0}>
        <Text as="h1" fontSize={['lg', 'xl']} fontWeight={600} color="redesign.heading" noOfLines={1}>
          {nameOf(meta, id, t)}
        </Text>
        <Text fontSize="xs" color="redesign.text.muted" noOfLines={1}>{line}</Text>
      </Box>
    </Flex>
  );
}

// A check that could not finish (a preview over its time, the ledger changed
// since it was measured): say so, and offer a new one.
function CheckFailed({ status, onRetry }) {
  const { t } = useTranslation('repairs');
  return (
    <VStack py={12} spacing={3} textAlign="center" maxW="520px" mx="auto">
      <Text fontSize="md" fontWeight={600} color="redesign.heading">{t('checkup.failed.title')}</Text>
      <Text fontSize="sm" color="redesign.text">
        {t(status === 'stale' ? 'checkup.failed.stale' : 'checkup.failed.failed')}
      </Text>
      <Button size="sm" colorScheme="blue" onClick={onRetry}>{t('checkup.failed.retry')}</Button>
    </VStack>
  );
}

// One statement of the checkup: its check, reviewed and applied, or what was
// applied to it.
function StatementCheckup({ sourceFileId, flags, meta, names, next, alone, onSettled, onContinue, onBack }) {
  const { t } = useTranslation('repairs');
  const toast = useToast();
  const isPhone = useIsPhone();
  const { run, phase, error, apply, undo, recheck } = useStatementCheck(sourceFileId, flags);
  const [busy, setBusy] = useState(null);
  const details = useMovementDetails(movementIds(run));

  // What each item starts as comes from the run; the Owner's clicks are kept
  // on top, for that run only, so a new answer from the server starts over.
  const runKey = run ? `${run.id}:${run.status}` : null;
  const [picked, setPicked] = useState({ key: null, values: {} });
  const base = useMemo(() => (run ? initialChoices(run.items) : {}), [run]);
  const choices = useMemo(
    () => ({ ...base, ...(picked.key === runKey ? picked.values : {}) }),
    [base, picked, runKey],
  );

  const ctx = useMemo(() => ({
    accountName: (id) => names.accounts[id],
    categoryName: (id) => names.categories[id],
  }), [names]);

  const onChoose = useCallback((id, value) => setPicked((prev) => ({
    key: runKey,
    values: { ...(prev.key === runKey ? prev.values : {}), [id]: value },
  })), [runKey]);
  // One answer for many questions at once (a bulk question).
  const onChooseMany = useCallback((ids, value) => setPicked((prev) => ({
    key: runKey,
    values: { ...(prev.key === runKey ? prev.values : {}), ...Object.fromEntries(ids.map((id) => [id, value])) },
  })), [runKey]);

  const fail = (err, action) => toast({
    status: 'error', description: t(errorKey(err, action)), duration: 5000, isClosable: true,
  });

  // `chosen` replaces the review's choices, as one decision for many changes does.
  const onApply = async (chosen = choices) => {
    setBusy('apply');
    try {
      const outcome = await apply(chosen);
      onSettled(sourceFileId);
      if (outcome?.stopped) {
        toast({
          status: 'warning',
          description: t(`toasts.stopped.${outcome.stopped}`, { defaultValue: t('toasts.error') }),
          duration: 6000,
          isClosable: true,
        });
      }
    } catch (err) {
      fail(err, 'apply');
    } finally {
      setBusy(null);
    }
  };

  // Nothing the check can fix, and the Owner leaves it as it is: it leaves
  // the queue until it changes, and the checkup moves on.
  const onReview = async () => {
    setBusy('review');
    try {
      await reviewStatement(sourceFileId);
      toast({ status: 'success', description: t('toasts.reviewed'), duration: 3000 });
      onSettled(sourceFileId);
      onContinue();
    } catch (err) {
      fail(err, 'review');
    } finally {
      setBusy(null);
    }
  };

  const onUndo = async () => {
    setBusy('undo');
    try {
      await undo();
      onSettled(sourceFileId, { undone: true });
    } catch (err) {
      fail(err, 'undo');
    } finally {
      setBusy(null);
    }
  };

  if (phase === 'loading' || phase === 'reading') {
    return (
      <Flex direction="column" align="center" gap={3} py={16} role="status">
        <Spinner color="redesign.accent.blue" />
        <Text fontSize="sm" color="redesign.text" textAlign="center" maxW="420px">
          {t(phase === 'reading' ? 'checkup.phase.reading' : 'checkup.phase.loading')}
        </Text>
      </Flex>
    );
  }
  if (phase === 'error' || !run) {
    return (
      <VStack py={12} spacing={3}>
        <Text fontSize="sm" fontWeight={600} color="redesign.heading">{t('checkup.phase.failed')}</Text>
        <Text fontSize="sm" color="redesign.text.muted">{t(errorKey(error, 'reread'))}</Text>
        <Button size="sm" onClick={() => recheck(flags)}>{t('checkup.phase.tryAgain')}</Button>
      </VStack>
    );
  }

  const decidable = run.kind === 'repair' && DECIDABLE_RUNS.includes(run.status);
  const done = ['applied', 'undone'].includes(run.status);
  const readAt = run.statement_diff?.reread && decidable ? run.created_at : null;
  // On the phone the sheet's header names the statement; its file line stays,
  // under the headline of a statement read twice.
  const twiceOnPhone = isPhone && decidable && Boolean(readTwice(run));
  return (
    <Box>
      {!isPhone ? (
        <StatementHeader id={sourceFileId} meta={meta} readAt={readAt} />
      ) : !done && !twiceOnPhone && (
        <Text textStyle="phoneMeta" color="phone.muted" mb={3} overflowWrap="anywhere">{statementLine(meta, readAt, t)}</Text>
      )}
      {!decidable && !done ? (
        <CheckFailed status={run.status} onRetry={() => recheck(flags)} />
      ) : decidable ? (
        <CheckupReview
          run={run}
          flags={flags}
          choices={choices}
          onChoose={onChoose}
          onChooseMany={onChooseMany}
          onApply={() => onApply()}
          onApplyAll={(items) => onApply(chooseAll(choices, items))}
          onNotNow={onBack}
          onReview={onReview}
          applying={busy === 'apply'}
          reviewing={busy === 'review'}
          details={details}
          names={names}
          ctx={ctx}
          isPhone={isPhone}
          metaLine={statementLine(meta, readAt, t)}
        />
      ) : (
        <CheckupDone
          run={run}
          name={nameOf(meta, sourceFileId, t)}
          details={details}
          names={names}
          ctx={ctx}
          onUndo={onUndo}
          undoing={busy === 'undo'}
          onCheckAgain={() => recheck(flags)}
          next={next}
          alone={alone}
          onContinue={onContinue}
          onBack={onBack}
          isPhone={isPhone}
        />
      )}
    </Box>
  );
}

/**
 * The statement checkup (`/source-files/checkup`): every statement that
 * needs a look, one at a time, newest upload first, or the one named by
 * `?statement=`, which may be any statement that can be checked (opened from
 * its menu, it is checked on its own, with no queue around it). Each is
 * checked against itself (read again when its page text is kept), reviewed,
 * applied with Undo, and followed by the next. Owner and Co-Owner only;
 * anyone else sees why.
 */
export default function StatementCheckupPage() {
  const { t } = useTranslation('repairs');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isPhone = useIsPhone();
  const { can } = useWorkspace();
  const allowed = can('repair.review');
  const { flags, reload } = useStatementRepairFlags(allowed);
  const statementNames = useStatementNames();
  const [reference, setReference] = useState({ accounts: [], categories: [] });
  const [queue, setQueue] = useState(null);
  const [settled, setSettled] = useState([]);
  const [skipped, setSkipped] = useState([]);
  // On the phone the checkup is a sheet; closing it leaves the checkup.
  const [sheetOpen, setSheetOpen] = useState(true);

  useEffect(() => { getReferenceData().then(setReference).catch(() => {}); }, []);
  useEffect(() => { if (flags && !queue) setQueue(checkupQueue(flags)); }, [flags, queue]);

  const names = useMemo(() => {
    const byId = (rows) => Object.fromEntries((rows || []).map((row) => [row.id, row.name]));
    return { accounts: byId(reference.accounts), categories: byId(reference.categories) };
  }, [reference]);

  // Done: applied or skipped here, or no longer needing a look.
  const finished = useMemo(() => [
    ...settled,
    ...(queue || []).filter((id) => flags?.[id] && !flags[id].needs_attention),
  ], [settled, queue, flags]);
  const requested = Number(params.get('statement')) || null;
  const current = requested || queue?.find((id) => !finished.includes(id)) || null;

  // The statement on screen is always the one in the address: opened
  // without one, the checkup names its first, so applying it shows what was
  // done instead of moving on.
  useEffect(() => {
    if (!requested && current) setParams({ statement: String(current) }, { replace: true });
  }, [requested, current, setParams]);
  // A statement opened on its own (not one that needs a look) is checked
  // without the queue: no progress, no "up next".
  const inQueue = Boolean(queue?.includes(current));
  const index = inQueue ? queue.indexOf(current) : -1;
  const done = inQueue ? queue.filter((id) => finished.includes(id)).length : 0;
  const nextId = inQueue ? nextInQueue(queue, current, finished) : null;
  const left = inQueue ? queue.filter((id) => id !== current && !finished.includes(id)).length : 0;

  // Once the sheet has stepped back over its own history entry, the phone
  // goes back to wherever the checkup was opened from; opened as the app's
  // first page, to the statements. Opened from inside another sheet, the
  // router numbers the entry NaN: there is a page to go back to then.
  useEffect(() => {
    if (!isPhone || sheetOpen) return undefined;
    let active = true;
    sheetBacksSettled().then(() => {
      if (!active) return;
      const idx = window.history.state?.idx;
      if (idx === 0 || idx == null) navigate(LIST_PATH, { replace: true });
      else navigate(-1);
    });
    return () => { active = false; };
  }, [isPhone, sheetOpen, navigate]);

  const leave = () => (isPhone ? setSheetOpen(false) : navigate(LIST_PATH));
  // The phone's sheet holds one history entry, so moving on replaces the
  // statement in the address rather than adding one per statement.
  const goTo = (id) => (id ? setParams({ statement: String(id) }, { replace: isPhone }) : leave());
  const onSettled = (id, { undone = false } = {}) => {
    setSettled((prev) => (undone ? prev.filter((other) => other !== id) : [...new Set([...prev, id])]));
    reload();
  };
  const onSkip = () => {
    setSettled((prev) => [...new Set([...prev, current])]);
    setSkipped((prev) => [...new Set([...prev, current])]);
    goTo(nextId);
  };

  let body;
  if (!allowed) {
    body = <Text fontSize="sm" color="redesign.text" py={12} textAlign="center">{t('errors.forbidden')}</Text>;
  } else if (!flags || !queue) {
    body = <Flex justify="center" py={16}><Spinner color="redesign.accent.blue" /></Flex>;
  } else if (!current || !flags[current]) {
    body = (
      <VStack py={12} spacing={3}>
        <Text fontSize="md" fontWeight={600} color="redesign.heading">{t('filter.empty')}</Text>
        <Button size="sm" variant="outline" onClick={leave}>{t('checkup.done.backToList')}</Button>
      </VStack>
    );
  } else {
    body = (
      <StatementCheckup
        key={current}
        sourceFileId={current}
        flags={flags[current]}
        meta={statementNames[current]}
        names={names}
        next={nextId ? { id: nextId, left, flags: flags[nextId], name: nameOf(statementNames[nextId], nextId, t) } : null}
        alone={!inQueue}
        onSettled={onSettled}
        onContinue={() => goTo(nextId)}
        onBack={leave}
      />
    );
  }

  const canSkip = Boolean(current && nextId && !finished.includes(current));
  if (isPhone) {
    // A statement just fixed shows the next one as the one up next.
    const active = finished.includes(current) ? nextId : current;
    return (
      <PhoneCheckupSheet
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title={current && flags?.[current] ? nameOf(statementNames[current], current, t) : t('checkup.top.label')}
        subtitle={inQueue
          ? t(finished.includes(current) ? 'checkup.phone.positionDone' : 'checkup.phone.position', { index: index + 1, count: queue.length })
          : undefined}
        onSkip={canSkip ? onSkip : undefined}
        segments={inQueue
          ? queue.map((id) => (finished.includes(id) && !skipped.includes(id) ? 'done' : id === active ? 'active' : 'todo'))
          : null}
      >
        {body}
      </PhoneCheckupSheet>
    );
  }

  return (
    <Box maxW="1040px" mx="auto" w="100%" px={[4, 6, 8]}>
      <TopBar
        index={index >= 0 ? index : null}
        count={inQueue ? queue.length : 0}
        done={done}
        onBack={() => navigate(LIST_PATH)}
        onSkip={onSkip}
        canSkip={canSkip}
      />
      {body}
    </Box>
  );
}
